import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, Settings } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

const SettingsDialog = () => {
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-4o-mini");

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setProvider(data.model_provider);
      setModel(data.model_name);
    } catch (e) {}
  };

  const save = async () => {
    try {
      await api.put("/admin/settings", { model_provider: provider, model_name: model });
      toast.success("Nastavení AI modelu uloženo");
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="settings-button" className="text-slate-600 hover:text-slate-900">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nastavení AI modelu</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <div className="flex gap-2">
              {["openai", "edenai"].map((p) => (
                <Button key={p} type="button" variant={provider === p ? "default" : "outline"} size="sm"
                  data-testid={`provider-${p}-button`} onClick={() => setProvider(p)}>{p === "openai" ? "OpenAI (přímo)" : "EdenAI"}</Button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="model-name">Model</Label>
            <Input id="model-name" data-testid="model-name-input" value={model} onChange={(e) => setModel(e.target.value)}
              placeholder="např. gpt-4o-mini, gpt-4o, gpt-4.1" />
            <p className="text-xs text-slate-500">Model můžete kdykoli změnit — použije se pro další spuštěné audity.</p>
          </div>
          <Button onClick={save} data-testid="settings-save-button" className="w-full">Uložit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/dashboard" data-testid="nav-home-link" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>AuditŠVP</div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Diagnostika ŠVP vůči RVP PV</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="admin-link"
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900">
                Administrace
              </Link>
            )}
            {user?.role === "admin" && <SettingsDialog />}
            {!user && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="header-try-button"
                onClick={() => navigate("/start")}>
                Vyzkoušet na svém ŠVP
              </Button>
            )}
            {user && (
              <>
                <span className="hidden text-sm text-slate-600 sm:block" data-testid="user-email">{user.email}</span>
                <Button variant="outline" size="sm" data-testid="logout-button"
                  onClick={async () => { await logout(); navigate("/login"); }}>
                  <LogOut className="mr-1.5 h-3.5 w-3.5" /> Odhlásit
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
      <footer className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <p className="text-[11px] leading-relaxed text-slate-400" data-testid="disclaimer-text">
          AuditŠVP je diagnostický podpůrný nástroj — nenahrazuje stanovisko MŠMT, NPI ani ČŠI.
          · JustRight Stage s.r.o. · IČO: 08588023 · Zámostní 1155/27, Slezská Ostrava, 710 00 Ostrava
        </p>
      </footer>
    </div>
  );
};
